import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { firebaseApp } from "./firebaseApp";
/**
 * DownloadService - Handles Firebase Storage downloads for desktop app installers
 */
class DownloadService {
  constructor() {
    this.storage = getStorage(firebaseApp);
    this.installerPaths = {
      "darwin-arm64": process.env.REACT_APP_FIREBASE_ELECTRON_DARWIN_ARM64_URL,
      "darwin-x64": process.env.REACT_APP_FIREBASE_ELECTRON_DARWIN_X64_URL,
      "win32-x64": process.env.REACT_APP_FIREBASE_ELECTRON_WIN32_X64_URL,
      "linux-x64": process.env.REACT_APP_FIREBASE_ELECTRON_LINUX_X64_URL,
    };
  }

  /**
   * Gets download URL for specific platform installer
   * @param {string} platform - Platform identifier (e.g., 'darwin-arm64', 'win32-x64')
   * @returns {Promise<string>} Download URL
   */
  async getInstallerDownloadUrl(platform) {
    try {
      const installerPath = this.installerPaths[platform];
      if (!installerPath) {
        throw new Error(`Unsupported platform: ${platform}`);
      }

      if (installerPath.startsWith("http")) {
        return installerPath;
      }

      const storageRef = ref(this.storage, installerPath);
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.error("Error getting installer download URL:", error);
      throw error;
    }
  }

  /**
   * Detects user's platform and returns appropriate installer info
   * @returns {Object} Platform information with download details
   */
  detectPlatform() {
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform.toLowerCase();

    let platformInfo = {
      os: "unknown",
      arch: "x64",
      downloadKey: null,
      displayName: "Unknown Platform",
    };

    // Detect macOS
    if (platform.includes("mac") || userAgent.includes("mac")) {
      platformInfo.os = "darwin";
      // Detect Apple Silicon vs Intel
      if (
        userAgent.includes("mac os x 10_15") ||
        userAgent.includes("mac os x 11_") ||
        userAgent.includes("mac os x 12_") ||
        userAgent.includes("mac os x 13_") ||
        userAgent.includes("mac os x 14_") ||
        userAgent.includes("mac os x 15_")
      ) {
        // For newer macOS versions, assume ARM64 (Apple Silicon) is more common
        platformInfo.arch = "arm64";
        platformInfo.downloadKey = "darwin-arm64";
        platformInfo.displayName = "macOS (Apple Silicon)";
      } else {
        platformInfo.arch = "x64";
        platformInfo.downloadKey = "darwin-x64";
        platformInfo.displayName = "macOS (Intel)";
      }
    }
    // Detect Windows
    else if (platform.includes("win") || userAgent.includes("windows")) {
      platformInfo.os = "win32";
      platformInfo.arch = "x64";
      platformInfo.downloadKey = "win32-x64";
      platformInfo.displayName = "Windows";
    }
    // Detect Linux
    else if (platform.includes("linux") || userAgent.includes("linux")) {
      platformInfo.os = "linux";
      platformInfo.arch = "x64";
      platformInfo.downloadKey = "linux-x64";
      platformInfo.displayName = "Linux";
    }

    return platformInfo;
  }

  /**
   * Downloads installer for detected or specified platform
   * @param {string} [customPlatform] - Optional custom platform override
   * @returns {Promise<void>}
   */
  async downloadInstaller(customPlatform = null) {
    try {
      let platformInfo;

      if (customPlatform) {
        // Create proper platform info for custom platform
        platformInfo = { downloadKey: customPlatform };

        // Add additional info for macOS platforms
        if (customPlatform.startsWith("darwin-")) {
          platformInfo.os = "darwin";
          platformInfo.arch = customPlatform.includes("arm64")
            ? "arm64"
            : "x64";
          platformInfo.displayName = customPlatform.includes("arm64")
            ? "macOS (Apple Silicon)"
            : "macOS (Intel)";
        }
      } else {
        platformInfo = this.detectPlatform();
      }

      if (!platformInfo.downloadKey) {
        throw new Error("Unable to detect platform or platform not supported");
      }

      const downloadUrl = await this.getInstallerDownloadUrl(
        platformInfo.downloadKey,
      );

      // Create invisible download link and trigger download
      const link = document.createElement("a");
      link.href = downloadUrl;

      // Use appropriate file extension based on platform
      const fileExtension = platformInfo.downloadKey.startsWith("darwin-")
        ? "dmg"
        : "zip";
      link.download = `Dynamic Desk-${platformInfo.downloadKey}.${fileExtension}`;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return { success: true, platform: platformInfo };
    } catch (error) {
      console.error("Download failed:", error);
      throw error;
    }
  }

  /**
   * Gets all available platforms with their download information
   * @returns {Array} List of available platforms
   */
  getAvailablePlatforms() {
    return [
      {
        key: "darwin-arm64",
        displayName: "macOS (Apple Silicon)",
        os: "darwin",
      },
      {
        key: "darwin-x64",
        displayName: "macOS (Intel)",
        os: "darwin",
      },
      {
        key: "win32-x64",
        displayName: "Windows",
        os: "win32",
      },
      {
        key: "linux-x64",
        displayName: "Linux",
        os: "linux",
      },
    ];
  }
}

export default new DownloadService();
